import React, { useState, useMemo, useRef, useEffect } from 'react';
import { IndianRupee, ChevronDown, Zap, Calendar as CalendarIcon, Info, ChevronLeft, ChevronRight, Search, Plus } from 'lucide-react';

/**
 * Custom self-contained PatternTool.
 * Uses the assignment method from the user's working snippet
 * while maintaining the Date Range Picker functionality.
 */

interface Nurse {
  id: string | number;
  full_name?: string;
  name?: string;
  first_name?: string;
  monthly_salary?: string | number;
}

interface PatternConfig {
  nurse_id: string;
  monthly_wage: string;
  daily_wage: string;
  client_charge: string;
  in_time: string;
  out_time: string;
}

interface PatternToolProps {
  shifts: any[];
  nurses: Nurse[];
  // Updated signature: child sends config record AND range details
  onApply: (config: Record<string, PatternConfig>, range: { startDate: string, endDate: string, totalDays: number }) => void;
  onClose: () => void;
  onQuickAdd?: () => void;
}

// --- DATE HELPERS ---
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Formats date to YYYY-MM-DD for API consumption
const toISODate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatStr = (date: Date, type: 'MMM dd' | 'full'): string => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const m = months[date.getMonth()];
  const d = date.getDate();
  const y = date.getFullYear();
  if (type === 'MMM dd') return `${m} ${d}`;
  return `${m} ${d}, ${y}`;
};

const differenceInDays = (d1: Date, d2: Date): number => {
  const date1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const date2 = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
  const diffTime = Math.abs(date1.getTime() - date2.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const isSameDay = (d1: Date, d2: Date): boolean => {
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

const isWithinRange = (date: Date, start: Date, end: Date): boolean => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return d >= s && d <= e;
};

// --- CUSTOM CALENDAR COMPONENT ---
const CustomDateRangePicker: React.FC<{ 
  startDate: Date, 
  endDate: Date, 
  onChange: (start: Date, end: Date) => void 
}> = ({ startDate, endDate, onChange }) => {
  const [viewDate, setViewDate] = useState(new Date(startDate.getFullYear(), startDate.getMonth(), 1));

  const daysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const handleDateClick = (day: number) => {
    // Set time to noon to avoid day-shifting during selection
    const clickedDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day, 12, 0, 0);
    
    // If range is already selected or clicked date is before current start, reset selection
    if (clickedDate < startDate || !isSameDay(startDate, endDate)) {
      onChange(clickedDate, clickedDate);
    } else {
      onChange(startDate, clickedDate);
    }
  };

  const changeMonth = (offset: number) => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1));
  };

  const days: (number | null)[] = [];
  const totalDays = daysInMonth(viewDate);
  const startPadding = firstDayOfMonth(viewDate);
  for (let i = 0; i < startPadding; i++) days.push(null);
  for (let i = 1; i <= totalDays; i++) days.push(i);

  const monthLabel = formatStr(viewDate, 'full').split(',')[0];

  return (
    <div style={{ width: '100%', backgroundColor: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', padding: '0 4px' }}>
        <button onClick={() => changeMonth(-1)} style={{ padding: '4px', border: 'none', background: 'none', cursor: 'pointer', color: '#d97706' }}>
          <ChevronLeft size={16} />
        </button>
        <span style={{ fontSize: '11px', fontWeight: 900, color: '#92400e', textTransform: 'uppercase' }}>{monthLabel}</span>
        <button onClick={() => changeMonth(1)} style={{ padding: '4px', border: 'none', background: 'none', cursor: 'pointer', color: '#d97706' }}>
          <ChevronRight size={16} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center' }}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => (
          <div key={d} style={{ fontSize: '9px', fontWeight: 900, color: '#f59e0b', paddingBottom: '6px' }}>{d}</div>
        ))}
        {days.map((day, idx) => {
          if (day === null) return <div key={`pad-${idx}`} />;
          const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day, 12, 0, 0);
          const isSelectedStart = isSameDay(date, startDate);
          const isSelectedEnd = isSameDay(date, endDate);
          const inRange = isWithinRange(date, startDate, endDate);
          const cellStyle: React.CSSProperties = {
            position: 'relative', padding: '6px 0', cursor: 'pointer', fontSize: '11px', fontWeight: 700, transition: 'all 0.2s', borderRadius: isSelectedStart || isSelectedEnd ? '6px' : '0',
            backgroundColor: isSelectedStart || isSelectedEnd ? '#d97706' : inRange ? '#fff7ed' : 'transparent',
            color: isSelectedStart || isSelectedEnd ? 'white' : inRange ? '#9a3412' : '#475569',
          };
          return <div key={day} onClick={() => handleDateClick(day)} style={cellStyle}>{day}</div>;
        })}
      </div>
    </div>
  );
};

const SESSION_COLORS = [
  { dot: '#2563eb' }, { dot: '#ea580c' }, { dot: '#4f46e5' }, 
  { dot: '#059669' }, { dot: '#9333ea' }, { dot: '#e11d48' }
];

const TimePicker12H: React.FC<{ value: string; onChange: (val: string) => void }> = ({ value, onChange }) => {
  const parseTime = (val: string) => {
    if (!val) return { hour: '12', minute: '00', period: 'AM' };
    const [h, m] = val.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    let hour = h % 12;
    if (hour === 0) hour = 12;
    return { hour: hour.toString().padStart(2, '0'), minute: m ? m.toString().padStart(2, '0') : '00', period };
  };
  const { hour, minute, period } = parseTime(value);
  const updateTime = (newHour: string, newMinute: string, newPeriod: string) => {
    let h = parseInt(newHour, 10);
    if (newPeriod === 'PM' && h !== 12) h += 12;
    if (newPeriod === 'AM' && h === 12) h = 0;
    onChange(`${h.toString().padStart(2, '0')}:${newMinute}`);
  };
  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));
  const selectStyle: React.CSSProperties = { flex: 1, background: 'transparent', border: 'none', padding: '0.2rem', fontSize: '0.75rem', fontWeight: 700, outline: 'none', appearance: 'none', textAlign: 'center', cursor: 'pointer', boxSizing: 'border-box' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #fde68a', borderRadius: '0.4rem', backgroundColor: 'white', overflow: 'hidden', height: '2rem', boxSizing: 'border-box' }}>
      <select value={hour} onChange={(e) => updateTime(e.target.value, minute, period)} style={selectStyle}>
        {hours.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span style={{ color: '#fde68a', fontWeight: 800, fontSize: '0.75rem' }}>:</span>
      <select value={minute} onChange={(e) => updateTime(hour, e.target.value, period)} style={selectStyle}>
        {minutes.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
      <div style={{ position: 'relative', height: '100%', minWidth: '2.5rem' }}>
        <select value={period} onChange={(e) => updateTime(hour, minute, e.target.value)} style={{ backgroundColor: '#fffbeb', border: 'none', borderLeft: '1px solid #fde68a', padding: '0 0.4rem', height: '100%', width: '100%', fontSize: '0.65rem', fontWeight: 900, color: '#b45309', outline: 'none', cursor: 'pointer', boxSizing: 'border-box', appearance: 'none', textAlign: 'center', textTransform: 'uppercase' }}>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
};

// --- UPDATED DROPDOWN COMPONENT ---
const SearchableNurseDropdown = ({ nurses, value, onChange, placeholder, onQuickAdd }: { nurses: Nurse[], value: string, onChange: (val: string) => void, placeholder: string, onQuickAdd?: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = nurses.filter(n => (n.full_name || n.name || n.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()));
  const selected = nurses.find(n => n.id.toString() === value);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        style={{ width: '100%', padding: '0.6rem', backgroundColor: 'white', border: '1px solid #fde68a', borderRadius: '0.5rem', fontSize: '0.8rem', fontWeight: 700, color: selected ? '#334155' : '#94a3b8', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxSizing: 'border-box' }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? (selected.full_name || selected.name || selected.first_name) : placeholder}
        </span>
        <ChevronDown size={14} color="#d97706" />
      </div>

      {isOpen && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '0.5rem', marginTop: '4px', zIndex: 50, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          
          {/* Search Input */}
          <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Search size={14} color="#94a3b8" />
            <input 
              autoFocus 
              type="text" 
              placeholder="Search by name..." 
              value={searchTerm} 
              onChange={e => setSearchTerm(e.target.value)} 
              style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.75rem', color: '#334155' }} 
            />
          </div>

          {/* Scrollable List */}
          <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
            {filtered.map(n => (
              <div 
                key={n.id} 
                onClick={() => { onChange(n.id.toString()); setIsOpen(false); setSearchTerm(''); }}
                style={{ padding: '10px 12px', fontSize: '0.8rem', cursor: 'pointer', borderBottom: '1px solid #f8fafc', color: value === n.id.toString() ? '#d97706' : '#334155', fontWeight: value === n.id.toString() ? 700 : 500 }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                {n.full_name || n.name || n.first_name}
              </div>
            ))}
            {filtered.length === 0 && <div style={{ padding: '10px', fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center' }}>No nurses found</div>}
          </div>

          {/* Quick Add Button (Pinned to Bottom) */}
          {onQuickAdd && (
            <div 
              onClick={() => { setIsOpen(false); onQuickAdd(); }}
              style={{ padding: '10px', textAlign: 'center', color: '#2563eb', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
            >
              <Plus size={14} strokeWidth={3} /> QUICK ADD NEW NURSE
            </div>
          )}

        </div>
      )}
    </div>
  );
};

// --- ADD THIS HELPER FUNCTION ---
const getDefaultTimes = (shiftType: string) => {
  const lower = shiftType.toLowerCase();
  if (lower.includes('08 morning') || lower.includes('8 morning')) {
    return { in_time: '06:00', out_time: '14:00' }; // 6 AM to 2 PM
  }
  if (lower.includes('08 afternoon') || lower.includes('8 afternoon')) {
    return { in_time: '14:00', out_time: '22:00' }; // 2 PM to 10 PM
  }
  if (lower.includes('08 night') || lower.includes('8 night')) {
    return { in_time: '22:00', out_time: '06:00' }; // 10 PM to 6 AM
  }
  if (lower.includes('12 day')) {
    return { in_time: '08:00', out_time: '20:00' }; // 8 AM to 8 PM
  }
  if (lower.includes('12 night')) {
    return { in_time: '20:00', out_time: '08:00' }; // 8 PM to 8 AM
  }
  if (lower.includes('24')) {
    return { in_time: '08:00', out_time: '08:00' }; // 8 AM to 8 AM (24 hrs)
  }
  return { in_time: '08:00', out_time: '20:00' }; // Default fallback
};

const PatternTool: React.FC<PatternToolProps> = ({ shifts, nurses, onApply, onClose, onQuickAdd }) => {
  const [patternConfig, setPatternConfig] = useState<Record<string, PatternConfig>>({});
  
  // Normalize initial dates
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = addDays(new Date(), 6);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const handleNurseSelect = (shiftId: string, val: string) => {
    const selectedNurse = nurses.find(n => n.id.toString() === val);
    const monthly = selectedNurse?.monthly_salary ? parseFloat(selectedNurse.monthly_salary.toString()) : 0;
    const daily = monthly ? Math.round(monthly / 30).toString() : '';

    const shiftType = shifts.find(sh => sh.id === shiftId)?.type || '';
    const defaultTimes = getDefaultTimes(shiftType);

    setPatternConfig(prev => ({
      ...prev,
      [shiftId]: {
        ...prev[shiftId],
        nurse_id: val,
        monthly_wage: monthly ? monthly.toString() : '',
        daily_wage: daily,
        client_charge: prev[shiftId]?.client_charge || '',
        in_time: prev[shiftId]?.in_time || defaultTimes.in_time,
        out_time: prev[shiftId]?.out_time || defaultTimes.out_time
      }
    }));
  };

  const handleMonthlyWageChange = (shiftId: string, val: string) => {
    const monthly = parseFloat(val);
    const daily = !isNaN(monthly) ? Math.round(monthly / 30).toString() : '';
    setPatternConfig(prev => ({
      ...prev,
      [shiftId]: { ...prev[shiftId], monthly_wage: val, daily_wage: daily }
    }));
  };

  const handleDailyWageChange = (shiftId: string, val: string) => {
    const daily = parseFloat(val);
    const monthly = !isNaN(daily) ? Math.round(daily * 30).toString() : '';
    setPatternConfig(prev => ({
      ...prev,
      [shiftId]: { ...prev[shiftId], daily_wage: val, monthly_wage: monthly }
    }));
  };

  const updateConfig = (shiftId: string, key: keyof PatternConfig, val: string) => {
    setPatternConfig(prev => ({
      ...prev,
      [shiftId]: { ...prev[shiftId], [key]: val }
    }));
  };

  const daysSelected = useMemo(() => differenceInDays(endDate, startDate) + 1, [startDate, endDate]);

  const handleApply = () => {
    // Send clean ISO strings (YYYY-MM-DD) to the parent for simple API usage
    const rangeDetails = { 
      startDate: toISODate(startDate), 
      endDate: toISODate(endDate), 
      totalDays: daysSelected 
    };

    onApply(patternConfig, rangeDetails);
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: { backgroundColor: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '1.25rem', overflowX: 'auto', boxSizing: 'border-box' },
    scrollArea: { display: 'flex', alignItems: 'flex-start', gap: '1.25rem', minWidth: 'max-content', paddingBottom: '0.5rem' },
    card: { width: '300px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem', backgroundColor: 'rgba(255, 255, 255, 0.5)', padding: '1rem', borderRadius: '0.75rem', border: '1px solid rgba(253, 230, 138, 0.5)', boxSizing: 'border-box' },
    dateCard: { width: '340px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem', backgroundColor: 'white', padding: '1.25rem', borderRadius: '0.75rem', border: '2px solid #fde68a', boxSizing: 'border-box', boxShadow: '0 10px 15px -3px rgba(217, 119, 6, 0.1)' },
    label: { fontSize: '10px', fontWeight: 900, color: '#d97706', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px', letterSpacing: '0.05em' },
    select: { width: '100%', padding: '0.6rem', backgroundColor: 'white', border: '1px solid #fde68a', borderRadius: '0.5rem', fontSize: '0.8rem', fontWeight: 700, color: '#334155', outline: 'none', cursor: 'pointer', appearance: 'none', boxSizing: 'border-box' },
    input: { width: '100%', padding: '0.5rem 0.6rem', backgroundColor: 'white', border: '1px solid #fde68a', borderRadius: '0.5rem', fontSize: '0.8rem', fontWeight: 700, color: '#334155', outline: 'none', boxSizing: 'border-box' },
    btnGroup: { display: 'flex', flexDirection: 'column', gap: '0.5rem', flexShrink: 0, alignSelf: 'center', marginLeft: '1rem' },
    btnCancel: { padding: '0.6rem 1rem', fontSize: '0.75rem', fontWeight: 900, color: '#b45309', textTransform: 'uppercase', cursor: 'pointer', background: 'none', border: 'none' },
    btnApply: { display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#d97706', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '0.75rem', fontSize: '0.75rem', fontWeight: 900, textTransform: 'uppercase', cursor: 'pointer', border: 'none', boxShadow: '0 4px 10px -2px rgba(217, 119, 6, 0.3)' },
    summaryBox: { backgroundColor: '#fff7ed', padding: '0.6rem 0.8rem', borderRadius: '0.5rem', border: '1px dashed #fdba74', marginBottom: '0.4rem' }
  };

  const Label = ({ children }: { children: React.ReactNode }) => (
    <label style={styles.label}>{children}</label>
  );

  return (
    <div style={styles.container}>
      <div style={styles.scrollArea}>
        <div style={styles.dateCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
             <Label><CalendarIcon size={12} /> 1. Select Duration</Label>
             <span style={{ fontSize: '10px', background: '#d97706', color: 'white', padding: '2px 6px', borderRadius: '10px', fontWeight: 800 }}>
               {daysSelected} Days
             </span>
          </div>
          <div style={styles.summaryBox}>
            <div style={{ fontSize: '11px', fontWeight: 800, color: '#9a3412', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <span>{formatStr(startDate, 'MMM dd')}</span>
               <span style={{ opacity: 0.3 }}>→</span>
               <span>{formatStr(endDate, 'full')}</span>
            </div>
          </div>
          <div style={{ padding: '0.75rem', border: '1px solid #fef3c7', borderRadius: '0.75rem', backgroundColor: '#fff' }}>
            <CustomDateRangePicker 
              startDate={startDate} 
              endDate={endDate} 
              onChange={(s, e) => { 
                setStartDate(s); 
                setEndDate(e); 
              }} 
            />
          </div>
          <p style={{ fontSize: '10px', color: '#b45309', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '0.5rem' }}>
             <Info size={10} /> Click start date then end date
          </p>
        </div>
        {shifts.map((s, idx) => {
          const colorTheme = SESSION_COLORS[idx % SESSION_COLORS.length];
          const defaultTimes = getDefaultTimes(s.type);
          const config = patternConfig[s.id] || { 
            nurse_id: '', 
            monthly_wage: '', 
            daily_wage: '', 
            client_charge: '', 
            in_time: defaultTimes.in_time, 
            out_time: defaultTimes.out_time
          };
          return (
            <div key={s.id} style={styles.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid rgba(217, 119, 6, 0.1)', paddingBottom: '0.5rem', marginBottom: '0.25rem' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: colorTheme.dot }}></div>
                <span style={{ fontSize: '11px', fontWeight: 900, color: '#92400e', textTransform: 'uppercase' }}>{s.label || s.type}</span>
              </div>
              <div>
                <Label>Primary Nurse</Label>
                <div style={{ position: 'relative' }}>
                  <SearchableNurseDropdown 
                    nurses={nurses} 
                    value={config.nurse_id} 
                    onChange={(val) => handleNurseSelect(s.id, val)} 
                    placeholder="Choose Nurse..." 
                    onQuickAdd={onQuickAdd} 
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <Label>Monthly</Label>
                  <div style={{ position: 'relative' }}>
                    <input type="number" style={{ ...styles.input, paddingLeft: '1.5rem' }} value={config.monthly_wage} onChange={(e) => handleMonthlyWageChange(s.id, e.target.value)} placeholder="0" />
                    <IndianRupee size={10} style={{ position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', color: '#d97706' }} />
                  </div>
                </div>
                <div>
                  <Label>Daily Rate</Label>
                  <input type="number" style={styles.input} value={config.daily_wage} onChange={(e) => handleDailyWageChange(s.id, e.target.value)} placeholder="0" />
                </div>
              </div>
              <div>
                <Label>Client Charge</Label>
                <input type="number" style={styles.input} value={config.client_charge} onChange={(e) => updateConfig(s.id, 'client_charge', e.target.value)} placeholder="Charge per day" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '4px' }}>
                <div>
                  <Label>In Time</Label>
                  <TimePicker12H value={config.in_time} onChange={(val) => updateConfig(s.id, 'in_time', val)} />
                </div>
                <div>
                  <Label>Out Time</Label>
                  <TimePicker12H value={config.out_time} onChange={(val) => updateConfig(s.id, 'out_time', val)} />
                </div>
              </div>
            </div>
          );
        })}
        <div style={styles.btnGroup}>
          <button onClick={handleApply} style={styles.btnApply}>
            <Zap size={14} /> Fill {daysSelected} Days
          </button>
          <button onClick={onClose} style={styles.btnCancel}>Discard</button>
        </div>
      </div>
    </div>
  );
};

export default PatternTool;